import { Checkbox, IconButton, Switch, TableCell, TableRow, Tooltip } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import Skeleton from '@material-ui/lab/Skeleton';
import React, { useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { Link } from 'react-router-dom';
import { track } from '~/src/dash/analytics/analytics';
import { IUser } from '~/src/dash/api/console-api';

import {
  CELL_STYLE,
  IListViewTableRow,
  ListViewTableDataCellRenderType,
  renderValue,
} from '~/src/dash/components/tables/ListViewTable/common';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { useListViewTableStore } from '~/src/dash/components/tables/ListViewTable/context';
import {
  Col,
  Cols,
  Row,
  Rows,
  spacing,
  Text,
  TKey,
  TooltipWithTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const getSkeletonHeight = (
  displayNameWithID: boolean | undefined,
  isExpanded: boolean | undefined,
) => {
  if (displayNameWithID) return '48px';
  if (!isExpanded) return '23px';
  return 'auto';
};

interface ILinkWrapperProps {
  id: string;
  enableTrackingOpenObject: boolean;
  isSuperUser: boolean;
  user: IUser;
  tableName: string;
  getItemSettingsRoute?: (id: string) => string;
  renderType: ListViewTableDataCellRenderType;
  children: any;
  shouldOpenLinkInNewTab?: boolean;
}

const LinkWrapper = ({
  id,
  getItemSettingsRoute,
  renderType,
  enableTrackingOpenObject,
  isSuperUser,
  user,
  tableName,
  children,
  shouldOpenLinkInNewTab,
}: ILinkWrapperProps): React.ReactNode => {
  if (renderType === 'name_link' && getItemSettingsRoute) {
    return (
      <Link
        to={getItemSettingsRoute(id)}
        {...(shouldOpenLinkInNewTab && {
          target: '_blank',
          rel: 'noopener noreferrer',
        })}
        {...(enableTrackingOpenObject && {
          onClick: () => {
            track(`${tableName} - Open Object`, { isSuperUser }, user);
          },
        })}
        style={{
          color: 'inherit',
          textDecoration: 'inherit',
        }}
      >
        {children}
      </Link>
    );
  }
  return <>{children}</>;
};

interface IListViewTableRowProps {
  activeColumnKeys: string[];
  backgroundColor: string;
  className: string;
  disableDeleteTooltipMessageTKey?: TKey;
  disableDelete?: (row: IListViewTableRow) => boolean;
  displayNameWithID?: boolean;
  enableActions?: boolean;
  enableCustomLeftColumn?: boolean;
  enableStatusSwitch?: boolean;
  enableClone?: boolean;
  enableTrackingOpenObject?: boolean;
  showDelete?: boolean;
  enableSelectCheckbox?: boolean;
  getItemSettingsRoute?: (id: string) => string;
  handleClick?: (row: any) => void;
  handleCloneItem?: (id: string) => void;
  handleOnChangeStatus?: (id: string, checked: boolean, row: IListViewTableRow) => void;
  handleOnChangeSelect?: (id: string, row: IListViewTableRow) => void;
  hasActiveSwitchSafeguard?: boolean;
  isActionPending?: boolean;
  isExpanded?: boolean;
  isItemSelected: boolean;
  isUpdating: boolean;
  isWriteAuthorized: boolean;
  shouldOpenLinkInNewTab?: boolean;
  row: IListViewTableRow;
  setActiveModalUpdateParams: (params: {
    id: string;
    checked: boolean;
    row: IListViewTableRow;
  }) => void;
  tableName?: string;
}

export const ListViewTableRow = ({
  activeColumnKeys,
  backgroundColor,
  className,
  disableDeleteTooltipMessageTKey,
  disableDelete = () => false,
  displayNameWithID,
  enableActions,
  enableCustomLeftColumn,
  enableStatusSwitch,
  enableClone,
  enableSelectCheckbox,
  enableTrackingOpenObject = false,
  getItemSettingsRoute,
  handleClick,
  handleCloneItem,
  handleOnChangeStatus,
  handleOnChangeSelect,
  hasActiveSwitchSafeguard,
  isActionPending,
  isExpanded,
  isItemSelected,
  isUpdating,
  isWriteAuthorized,
  shouldOpenLinkInNewTab,
  row,
  showDelete,
  setActiveModalUpdateParams,
  tableName,
}: IListViewTableRowProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { user, isSuperUser } = useBaseStore();
  // const [hoveredRow, setHoveredRow] = useState({} as IListViewTableRow); // to be implemented later

  const skeletonHeight = getSkeletonHeight(displayNameWithID, isExpanded);
  const {
    setIsDeleteModalActive,
    setDeleteModalID,
    // setDeleteModalIdx,
    setDeleteModalName,
    setIsActiveModalOpen,
    setActiveModalID,
    setActiveModalName,
  } = useListViewTableStore();

  const handleOnMouseOverRow = (
    _e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    _row: IListViewTableRow,
  ) => {
    unstable_batchedUpdates(() => {
      setIsHovered(true);
      // setHoveredRow(row); // to be implemented later
    });
  };

  const handleOnMouseLeaveRow = () => {
    setIsHovered(false);
    // setHoveredRow({} as IListViewTableRow); // to be implemented later
  };

  const id = `${row.ID.value}`;
  // get specific renderTypes
  const renderTypeStatus = enableStatusSwitch && row.Status.renderType;
  const renderTypeName = row.Name.renderType || 'name';
  const renderTypeActions = (row.Actions && row.Actions.renderType) || 'actions';
  const renderTypeCustomLeftColumn =
    (enableCustomLeftColumn && row.CustomLeftColumn && row.CustomLeftColumn.renderType) ||
    'name_link';
  const renderedValueCustomLeftColumn =
    row.CustomLeftColumn && renderValue(row.CustomLeftColumn.value, renderTypeCustomLeftColumn);
  const cellVerticalPadding = isExpanded ? '10px' : '4px';

  return (
    <TableRow
      hover
      onClick={() => (handleClick ? handleClick(row) : null)}
      role='checkbox'
      aria-checked={isItemSelected}
      tabIndex={-1}
      key={id}
      selected={isItemSelected}
      style={{
        cursor: handleClick ? 'pointer' : 'default',
        background: isHovered || isItemSelected ? publicaColors.veryLightGray : backgroundColor,
        verticalAlign: 'text-top',
      }}
      onMouseEnter={(e) => handleOnMouseOverRow(e, row)}
      onMouseLeave={handleOnMouseLeaveRow}
      className={`cypress-${className}-tablerow`}
    >
      {/* SELECTED CHECKBOX */}
      {enableSelectCheckbox && (
        <TableCell padding='checkbox' style={{ alignContent: 'center' }}>
          {isUpdating ? (
            <Skeleton variant='text' animation='wave' width='80%' height={skeletonHeight} />
          ) : (
            <Checkbox
              color='primary'
              checked={isItemSelected}
              onClick={(e: any) => e.stopPropagation()}
              onChange={() => {
                if (handleOnChangeSelect) {
                  handleOnChangeSelect(id, row);
                }
              }}
              disabled={!isWriteAuthorized || isActionPending || isUpdating}
              className={`cypress-${className}-tablecheckbox-select-${row.ID.value}`}
              style={{
                transform: 'scale(0.75)',
              }}
              id={id}
            />
          )}
        </TableCell>
      )}
      {/* STATUS SWITCH */}
      {enableStatusSwitch && (
        <TableCell
          align='left'
          style={{
            padding: `${cellVerticalPadding} 0px`,
            ...(renderTypeStatus ? CELL_STYLE[renderTypeStatus] : {}),
            ...(row.Status.cellStyle || {}),
          }}
        >
          {/* custom jsx is disabled here */}
          {isUpdating ? (
            <Skeleton variant='text' animation='wave' width='80%' height={skeletonHeight} />
          ) : (
            <Switch
              checked={row.Status.value as boolean}
              disabled={!isWriteAuthorized || isActionPending || isUpdating}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                if (!hasActiveSwitchSafeguard && handleOnChangeStatus) {
                  handleOnChangeStatus(row.ID.value as string, e.target.checked, row);
                }
              }}
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.stopPropagation();
                if (hasActiveSwitchSafeguard) {
                  unstable_batchedUpdates(() => {
                    setIsActiveModalOpen(true);
                    setActiveModalID(`${row.ID.value}`);
                    setActiveModalName(row.Name.value as string);
                    setActiveModalUpdateParams({
                      id: row.ID.value as string,
                      checked: !(row.Status.value as boolean),
                      row,
                    });
                  });
                }
              }}
              color='secondary'
              size='small'
              className={`cypress-${className}-tableswitch-status-${row.ID.value}`}
              id={id}
            />
          )}
        </TableCell>
      )}
      {/* CUSTOM LEFT COLUMN */}
      {enableCustomLeftColumn && (
        <TableCell
          align='left'
          style={{
            padding: `${cellVerticalPadding} 0px`,
            ...(renderTypeCustomLeftColumn ? CELL_STYLE[renderTypeCustomLeftColumn] : {}),
            ...(row.CustomLeftColumn.cellStyle || {}),
          }}
        >
          {isUpdating ? (
            <Skeleton variant='text' animation='wave' height={skeletonHeight} />
          ) : (
            (row.CustomLeftColumn.jsx &&
              row.CustomLeftColumn.jsx({ value: renderedValueCustomLeftColumn, row })) ||
            renderedValueCustomLeftColumn
          )}
        </TableCell>
      )}
      {/* NAME */}
      <TableCell
        component='th'
        id={id}
        scope='row'
        padding='none'
        style={{
          position: 'sticky',
          top: 1,
          left: 0,
          background: isHovered || isItemSelected ? publicaColors.veryLightGray : backgroundColor,
          borderBottom: `1px solid ${publicaColors.lightGray}`,
          padding: `${cellVerticalPadding} 0px`,
          minWidth: '192px',
          maxWidth: '240px',
          zIndex: 1,
          wordBreak: 'break-all',
          overflowWrap: 'anywhere',
          verticalAlign: 'text-top',
          fontWeight: 500,
          ...CELL_STYLE[renderTypeName],
          ...(row.Name.cellStyle || {}),
        }}
      >
        {isUpdating ? (
          <Skeleton variant='text' animation='wave' height={skeletonHeight} />
        ) : (
          <LinkWrapper
            id={id}
            enableTrackingOpenObject={enableTrackingOpenObject}
            tableName={tableName || className}
            isSuperUser={isSuperUser}
            user={user.user}
            getItemSettingsRoute={getItemSettingsRoute}
            renderType={row.Name.renderType || 'name'}
            shouldOpenLinkInNewTab={shouldOpenLinkInNewTab}
          >
            <Rows>
              <Row noPadding>
                <Cols gap={spacing.extraSmall} style={{ alignItems: 'center' }}>
                  <Col style={{ minWidth: 0 }}>
                    {row.Name.jsx
                      ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                        row.Name.jsx({
                          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                          value: renderValue(row.Name.value, row.Name.renderType),
                          row,
                        })
                      : // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                        renderValue(row.Name.value, row.Name.renderType)}
                  </Col>
                  {row.Name.renderType === 'name_link' &&
                    getItemSettingsRoute &&
                    shouldOpenLinkInNewTab && (
                      <Col displayFlex>
                        <IconV2 name='externalLink' small color={publicaColors.blue} />
                      </Col>
                    )}
                </Cols>
              </Row>
              {displayNameWithID && (
                <Row noPadding>
                  <Text extraSmall muted style={{ paddingBottom: '3px' }}>
                    {`ID: ${row.ID.value}`}
                  </Text>
                </Row>
              )}
            </Rows>
          </LinkWrapper>
        )}
      </TableCell>
      {/* DATA CELLS */}
      {activeColumnKeys.map((key) => {
        if (row[key] === undefined) {
          return;
        }
        const { jsx, cellStyle, renderType, value } = row[key];
        const renderedValue = renderValue(value, renderType);
        const styleOverrides = {
          ...(renderType ? CELL_STYLE[renderType] : {}),
          ...(cellStyle || {}),
        };

        return (
          <TableCell
            align='right'
            style={{
              paddingTop: cellVerticalPadding,
              paddingRight: '26px',
              paddingBottom: cellVerticalPadding,
              paddingLeft: '8px',
              verticalAlign: 'text-top',
              wordBreak: 'normal',
              overflowWrap: 'anywhere',
              ...styleOverrides,
            }}
          >
            {isUpdating ? (
              <Skeleton variant='text' animation='wave' height={skeletonHeight} />
            ) : (
              (jsx && jsx({ value: renderedValue, row })) || renderedValue
            )}
          </TableCell>
        );
      })}
      {/* ACTIONS COLUMN */}
      {enableActions && row.Actions && (
        <TableCell
          align='right'
          style={{
            padding: 2,
            ...CELL_STYLE[renderTypeActions],
            ...(row.Actions.cellStyle || {}),
          }}
        >
          {isUpdating ? (
            <Skeleton variant='text' animation='wave' height={skeletonHeight} />
          ) : (
            <Cols style={{ justifyContent: 'flex-end' }}>
              {enableClone && (
                <Col>
                  <Tooltip title='Clone'>
                    <IconButton
                      style={{ padding: '4px' }}
                      aria-label='Clone'
                      disabled={!isWriteAuthorized || isActionPending || isUpdating}
                      onClick={() => {
                        if (handleCloneItem) {
                          handleCloneItem(row.ID.value as string);
                        }
                      }}
                      className={`cypress-${className}-tableaction-clone-${row.Name.value}`}
                    >
                      <FileCopyIcon />
                    </IconButton>
                  </Tooltip>
                </Col>
              )}
              {showDelete && (
                <Col>
                  <TooltipWithTranslation
                    tKey={
                      disableDelete(row) && disableDeleteTooltipMessageTKey
                        ? disableDeleteTooltipMessageTKey
                        : 'Delete'
                    }
                  >
                    <div>
                      <IconButton
                        style={{ padding: '4px' }}
                        aria-label='Delete'
                        disabled={
                          !isWriteAuthorized || isActionPending || isUpdating || disableDelete(row)
                        }
                        onClick={() => {
                          unstable_batchedUpdates(() => {
                            setIsDeleteModalActive(true);
                            setDeleteModalID(`${row.ID.value}`);
                            setDeleteModalName(row.Name.value as string);
                          });
                        }}
                        className={`cypress-${className}-tableaction-delete-${row.Name.value}`}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </TooltipWithTranslation>
                </Col>
              )}
            </Cols>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};
